import { z } from "zod";
import { optional, photoUrl, storableText, whereTaken } from "./appointments";
import { recordId } from "./contracts";
import { photoContentType } from "./photo-types";
import { photoSlot } from "./services";

export { photoContentType, type PhotoContentType } from "./photo-types";

export const noteAudience = z.enum(["customer", "subcontractor", "worker"]);
export type NoteAudience = z.infer<typeof noteAudience>;

export const noteSharing = z.union([z.literal("everyone"), z.literal("internal"), z.array(noteAudience).min(1)]);
export type NoteSharing = z.infer<typeof noteSharing>;

export const historyKind = z.enum(["history", "note"]);
export type HistoryKind = z.infer<typeof historyKind>;

export const historyType = z.enum(["booked", "status_changed", "assignment", "service_change_requested", "note", "other"]);
export type HistoryType = z.infer<typeof historyType>;

export const historyEntry = z.object({
  id: recordId,
  kind: historyKind,
  type: historyType,
  message: z.string(),
  author: z.object({ id: recordId, name: z.string().nullable() }).nullable(),
  at: z.string(),
  audience: noteSharing.optional(),
  reasons: z.array(z.string()).min(1).optional(),
});
export type HistoryEntry = z.infer<typeof historyEntry>;

export const historyQuery = z.object({ actingOrganization: optional(recordId.toLowerCase()) });
export type HistoryQuery = z.infer<typeof historyQuery>;

export const appointmentHistory = z.object({ items: z.array(historyEntry) });
export type AppointmentHistory = z.infer<typeof appointmentHistory>;

export const NOTE_MAX_LENGTH = 4000;

export const noteRequest = z
  .object({
    message: storableText(NOTE_MAX_LENGTH),
    audience: z.array(noteAudience).max(noteAudience.options.length).optional(),
    actingOrganization: optional(recordId.toLowerCase()),
  })
  .strict();
export type NoteRequest = z.infer<typeof noteRequest>;

export const checklistAnswer = z.object({
  id: recordId,
  by: z.object({ id: recordId, name: z.string().nullable() }).nullable(),
  at: z.string(),
});
export type ChecklistAnswer = z.infer<typeof checklistAnswer>;

export const checklistItem = z.object({
  task: z.object({ id: recordId, name: z.string() }),
  category: z.object({ id: recordId, name: z.string() }),
  done: z.boolean(),
  answer: checklistAnswer.nullable(),
});
export type ChecklistItem = z.infer<typeof checklistItem>;

export const appointmentChecklist = z.object({ items: z.array(checklistItem) });
export type AppointmentChecklist = z.infer<typeof appointmentChecklist>;

export const tickRequest = z.object({ done: z.boolean() }).strict();
export type TickRequest = z.infer<typeof tickRequest>;

export const photoSide = z.enum(["before", "after"]);
export type PhotoSide = z.infer<typeof photoSide>;

export const photoMetadata = z
  .object({
    takenAt: z.string().datetime({ offset: true }).optional(),
    ...whereTaken,
  })
  .strict();
export type PhotoMetadata = z.infer<typeof photoMetadata>;

export const appointmentPhoto = z.object({
  id: recordId,
  workflow: recordId,
  side: photoSide,
  url: z.string(),
  metadata: photoMetadata,
  by: z.object({ id: recordId, name: z.string().nullable() }).nullable(),
  at: z.string(),
});
export type AppointmentPhoto = z.infer<typeof appointmentPhoto>;

export const appointmentPhotos = z.object({ items: z.array(appointmentPhoto) });
export const appointmentPhotoSlots = z.object({ items: z.array(photoSlot) });
export type AppointmentPhotoSlots = z.infer<typeof appointmentPhotoSlots>;
export type AppointmentPhotos = z.infer<typeof appointmentPhotos>;

export const photoRequest = z
  .object({
    workflow: recordId.toLowerCase(),
    side: photoSide,
    url: photoUrl,
    metadata: photoMetadata.optional(),
  })
  .strict();
export type PhotoRequest = z.infer<typeof photoRequest>;

export const slotPhotoPurpose = z.enum(["before", "after"]);
export const photoPurpose = z.enum([...slotPhotoPurpose.options, "extraBefore", "extraAfter", "clockIn", "clockOut"]);
export type PhotoPurpose = z.infer<typeof photoPurpose>;

const slotUpload = z.object({ purpose: slotPhotoPurpose, workflow: recordId.toLowerCase(), contentType: photoContentType }).strict();
const appointmentUpload = z.object({ purpose: photoPurpose.exclude(slotPhotoPurpose.options), contentType: photoContentType }).strict();

export const photoUploadRequest = z.union([slotUpload, appointmentUpload]);
export type PhotoUploadRequest = z.infer<typeof photoUploadRequest>;

export const photoUpload = z.object({ uploadUrl: z.string().url(), photoUrl: z.string().url() });
export type PhotoUpload = z.infer<typeof photoUpload>;
